import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { IconRestore, IconClose } from './icons'

const IconStack = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="5" rx="1" />
    <rect x="3" y="12" width="18" height="5" rx="1" />
  </svg>
)

// Minimized-window dock anchored in the left nav. A compact trigger shows the
// count; clicking opens a popup list (labels can be long, so they live in a
// menu, not inline). Each row restores on click, with a close button. Renders
// nothing when there are no minimized windows.
function GridDock({ items, getTitle, onRestore, onClose }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState(null)
  const btnRef = useRef(null)
  const popRef = useRef(null)

  useLayoutEffect(() => {
    if (!open) return undefined
    const place = () => {
      const rect = btnRef.current?.getBoundingClientRect()
      if (rect) setPos({ top: rect.top - 6, left: rect.left, width: rect.width })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const onDown = (event) => {
      if (popRef.current?.contains(event.target)) return
      if (btnRef.current?.contains(event.target)) return
      setOpen(false)
    }
    const onKey = (event) => event.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDown, true)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown, true)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Auto-close when nothing is left to show.
  useEffect(() => {
    if (!items.length) setOpen(false)
  }, [items.length])

  if (!items.length) return null

  return (
    <div className="grid-dock">
      <button
        type="button"
        ref={btnRef}
        className={'grid-dock-trigger' + (open ? ' is-active' : '')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="grid-dock-trigger-icon">
          <IconStack />
        </span>
        <span className="grid-dock-trigger-label">Minimized</span>
        <span className="grid-dock-count">{items.length}</span>
      </button>

      {open &&
        pos &&
        createPortal(
          <div
            className="grid-dock-pop"
            ref={popRef}
            style={{ top: pos.top, left: pos.left, minWidth: pos.width }}
            role="menu"
          >
            {items.map((win) => {
              const title = getTitle(win)
              return (
                <div className="grid-dock-row" key={win.id}>
                  <button
                    type="button"
                    className="grid-dock-restore"
                    role="menuitem"
                    title={`Restore ${title}`}
                    onClick={() => {
                      onRestore(win.id)
                      setOpen(false)
                    }}
                  >
                    <span className="grid-dock-row-icon">
                      <IconRestore />
                    </span>
                    <span className="grid-dock-row-label">{title}</span>
                  </button>
                  <button
                    type="button"
                    className="grid-dock-close"
                    title="Close window"
                    aria-label="Close window"
                    onClick={() => onClose(win.id)}
                  >
                    <IconClose />
                  </button>
                </div>
              )
            })}
          </div>,
          document.body
        )}
    </div>
  )
}

export default GridDock
